import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { menuCategories, siteInfo } from "../data/mockData";
import { Utensils, Beer, Clock, MapPin, ArrowRight, ExternalLink, CalendarCheck } from "lucide-react";
import useInView from "../hooks/useInView";

const PubGrill = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [menuRef, menuInView] = useInView();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(/images/x7a6p326_pubhero.png)",
          }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
            Dining
          </span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">
            Pub & Grill
          </h1>
          <p className="text-white/70 text-lg mt-4 max-w-xl">
            Great food, cold drinks, and stunning views of the course. The
            perfect way to start or end your round.
          </p>
        </div>
      </section>

      {/* Info Bar */}
      <section className="bg-[#1B2A4A] py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap justify-center gap-8 md:gap-16">
          <div className="flex items-center gap-3 text-white">
            <Clock size={20} className="text-[#C9A96E]" />
            <span className="text-sm">Open Daily: 8 AM - 8 PM</span>
          </div>
          <div className="flex items-center gap-3 text-white">
            <MapPin size={20} className="text-[#C9A96E]" />
            <span className="text-sm">Located in the Clubhouse</span>
          </div>
          <a
            href="/order"
            className="flex items-center gap-2 text-[#C9A96E] hover:text-[#d4b97e] text-sm font-medium transition-colors"
          >
            <ExternalLink size={16} />
            Reserve a Table
          </a>
        </div>
      </section>

      {/* About */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
                The Experience
              </span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">
                Where Good Times Meet Great Food
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                The Pub & Grill at Willow Ridge is more than just a restaurant
                — it's the heart of the club. Whether you're celebrating a great
                round, catching the game with friends, or enjoying a casual
                family dinner, our welcoming atmosphere has something for
                everyone.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Our kitchen serves up classic pub favourites alongside chef
                specials, all made with fresh ingredients. Enjoy your meal on our
                patio with panoramic views of the course or relax inside our
                comfortable dining room.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 bg-[#f8f7f4] rounded-lg">
                  <Utensils size={24} className="text-[#C9A96E] mb-2" />
                  <div className="text-[#1B2A4A] font-semibold">Full Menu</div>
                  <div className="text-gray-500 text-sm">Starters to mains</div>
                </div>
                <div className="p-4 bg-[#f8f7f4] rounded-lg">
                  <Beer size={24} className="text-[#C9A96E] mb-2" />
                  <div className="text-[#1B2A4A] font-semibold">Full Bar</div>
                  <div className="text-gray-500 text-sm">Beer, wine & cocktails</div>
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img
                src="/images/dining.jpg"
                alt="Pub & Grill Interior"
                className="w-full h-[500px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Menu */}
      <section ref={menuRef} id="menu" className="py-24 bg-[#f8f7f4]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
              Our Menu
            </span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3">
              {menuCategories[activeCategory].name === "Breakfast" ? "Breakfast Menu" : "Lunch & Dinner Menu"}
            </h2>
            <p className="text-gray-500 mt-3 text-sm">2026 Season</p>
          </div>

          {/* Category Tabs - scrollable */}
          <div className="flex overflow-x-auto gap-2 mb-12 pb-2 scrollbar-hide">
            {menuCategories.map((cat, index) => (
              <button
                key={cat.name}
                onClick={() => setActiveCategory(index)}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap shrink-0 ${
                  activeCategory === index
                    ? "bg-[#1B2A4A] text-white shadow-md"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Category description if exists */}
          {menuCategories[activeCategory].description && (
            <p className="text-center text-gray-500 text-sm italic mb-8 -mt-4">
              {menuCategories[activeCategory].description}
            </p>
          )}

          {/* Menu Items */}
          <div className={`space-y-3 ${
            menuInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          } transition-all duration-700`}>
            {menuCategories[activeCategory].isSpecials ? (
              /* Weekly Specials layout */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {menuCategories[activeCategory].items.map((item) => (
                  <div key={item.name} className="bg-white rounded-xl p-6 hover:shadow-md transition-shadow duration-300 border-l-4 border-[#C9A96E]">
                    <h4 className="text-lg font-serif font-bold text-[#1B2A4A] mb-2">{item.name}</h4>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            ) : menuCategories[activeCategory].isBreakfast ? (
              /* Breakfast sectioned layout */
              <div className="space-y-8">
                {menuCategories[activeCategory].sections.map((section) => (
                  <div key={section.title || "addons"}>
                    {section.title && (
                      <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-4 pb-2 border-b-2 border-[#C9A96E]/30">
                        {section.title}
                      </h3>
                    )}
                    {section.isAddons ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {section.items.map((item) => (
                          <div key={item.name} className="bg-white rounded-xl p-4 flex justify-between items-center hover:shadow-md transition-shadow">
                            <span className="text-sm text-gray-700">{item.name}</span>
                            <span className="text-[#C9A96E] font-bold text-sm ml-3 shrink-0">{item.price}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {section.items.map((item) => (
                          <div key={item.name} className="bg-white rounded-xl p-6 flex justify-between items-start hover:shadow-md transition-shadow duration-300">
                            <div className="flex-1">
                              <h4 className="text-lg font-serif font-bold text-[#1B2A4A] mb-1">{item.name}</h4>
                              <p className="text-gray-500 text-sm">{item.description}</p>
                            </div>
                            <div className="text-[#C9A96E] font-bold text-base ml-6 shrink-0 whitespace-nowrap">{item.price}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Regular menu items */
              menuCategories[activeCategory].items.map((item) => (
                <div
                  key={item.name}
                  className="bg-white rounded-xl p-6 flex justify-between items-start hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex-1">
                    <h4 className="text-lg font-serif font-bold text-[#1B2A4A] mb-1">
                      {item.name}
                    </h4>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </div>
                  <div className="text-[#C9A96E] font-bold text-base ml-6 shrink-0 whitespace-nowrap">
                    {item.price}
                  </div>
                </div>
              ))
            )}
          </div>

          <p className="text-center text-gray-400 text-sm mt-8">
            Menu items subject to seasonal availability. Prices do not include tax.
          </p>
        </div>
      </section>

      {/* Order CTA */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            Ready to Dine?
          </h2>
          <p className="text-white/60 mb-8">
            Reserve a table or call ahead. We look
            forward to serving you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/order"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors duration-300"
            >
              Reserve a Table <ExternalLink size={18} />
            </a>
            <a
              href={`tel:${siteInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors duration-300"
            >
              Call to Order
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PubGrill;
